import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FiMail, FiLock, FiEye, FiEyeOff } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { Button } from '../../components/ui/Button';
import { Alert } from '../../components/ui/Alert';
import { AuthShell } from '../../components/auth/AuthShell';
import { GoogleButton } from '../../components/auth/GoogleButton';
import { Turnstile } from '../../components/Turnstile';
import { salvarRedirecionamento } from '../../lib/redirectPosLogin';

export function LoginPage() {
  const { signIn, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const destino = (location.state as { de?: string } | null)?.de ?? '/';

  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [google, setGoogle] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setErro(null);
    setCarregando(true);

    // Permite login por e-mail OU por CPF/CNPJ (item 2.66 do TR).
    let emailLogin = email.trim();
    if (!emailLogin.includes('@')) {
      const { data, error: errDoc } = await supabase.rpc('cidadao_email_por_login', { p_documento: emailLogin });
      if (errDoc || !data) {
        setCarregando(false);
        setErro('E-mail, CPF ou CNPJ não encontrado. Verifique os dados e tente novamente.');
        return;
      }
      emailLogin = data as string;
    }

    const { error } = await signIn(emailLogin, senha, captchaToken ?? undefined);
    setCarregando(false);
    if (error) {
      setErro(error);
      return;
    }
    navigate(destino, { replace: true });
  }

  async function entrarComGoogle() {
    setErro(null);
    setGoogle(true);
    // O OAuth recarrega a página inteira (volta para "/"), perdendo o state da
    // rota — guardamos o destino para o InicioGate retomar após o login.
    salvarRedirecionamento(destino);
    const { error } = await signInWithGoogle();
    if (error) {
      setErro(error);
      setGoogle(false);
    }
    // Em caso de sucesso, o navegador é redirecionado para o Google.
  }

  return (
    <AuthShell titulo="Entrar" subtitulo="Acesse sua conta no portal do cidadão">
      <form onSubmit={handleSubmit} className="space-y-4">
        {erro && <Alert variante="erro">{erro}</Alert>}

        <GoogleButton onClick={entrarComGoogle} loading={google} texto="Entrar com Google" />

        <div className="flex items-center gap-3 py-1">
          <div className="flex-1 h-px bg-slate-200" />
          <span className="text-xs text-slate-400">ou com e-mail</span>
          <div className="flex-1 h-px bg-slate-200" />
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1.5">E-mail, CPF ou CNPJ</label>
          <div className="relative">
            <FiMail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <input
              id="email"
              type="text"
              required
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="seu@email.com, CPF ou CNPJ"
              className="w-full pl-9 pr-3 h-11 bg-slate-50 border-2 border-slate-200 rounded-md text-slate-900 focus:bg-white focus:border-institutional outline-none transition"
            />
          </div>
        </div>

        <div>
          <div className="flex items-baseline justify-between mb-1.5">
            <label htmlFor="senha" className="block text-sm font-medium text-slate-700">Senha</label>
            <Link to="/recuperar-senha" className="text-xs text-institutional hover:underline">
              Esqueci minha senha
            </Link>
          </div>
          <div className="relative">
            <FiLock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <input
              id="senha"
              type={mostrarSenha ? 'text' : 'password'}
              required
              autoComplete="current-password"
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              placeholder="Sua senha"
              className="w-full pl-9 pr-10 h-11 bg-slate-50 border-2 border-slate-200 rounded-md text-slate-900 focus:bg-white focus:border-institutional outline-none transition"
            />
            <button
              type="button"
              onClick={() => setMostrarSenha((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              aria-label={mostrarSenha ? 'Ocultar senha' : 'Mostrar senha'}
            >
              {mostrarSenha ? <FiEyeOff size={16} /> : <FiEye size={16} />}
            </button>
          </div>
        </div>

        <Turnstile onToken={setCaptchaToken} />

        <Button type="submit" loading={carregando} fullWidth className="h-11">Entrar</Button>
      </form>

      <p className="text-center text-sm text-slate-600 mt-6">
        Ainda não tem conta?{' '}
        <Link to="/cadastro" className="text-institutional font-semibold hover:underline">
          Cadastre-se
        </Link>
      </p>
    </AuthShell>
  );
}
